import * as React from "react";
import { Link } from "react-router";

import { useDomainServices } from "@/providers/DomainServices";
import { BabylonPreview } from "@/shared/components/visualization/IngestAsAssetDialog/BabylonPreview";
import { useModelPreview } from "@/shared/components/visualization/IngestAsAssetDialog/useModelPreview";
import { ModelLoader } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import type { FileRecord } from "@/shared/domain/files";
import {
  useFilesAtPaths,
  useFilesWithExtensions,
} from "@/shared/domain/files/hooks";
import {
  ingestPhase,
  readVisualizationAsset,
  urdfFileTypes,
} from "@/shared/domain/urdf";
import {
  useVisualizationAsset,
  useVisualizationAssetFile,
} from "@/shared/domain/urdf/hooks";
import { Spinner } from "@/shared/foundation/primitives";

import styles from "./UrdfPreviewPanel.module.css";

interface UrdfPreviewPanelProps {
  /** The association whose URDFs are previewed: a device's or a device type's. */
  associationId: string;
  /**
   * A second association to look in, after `associationId`: the type a device
   * inherits configurations from.
   */
  inheritedAssociationId?: string;
  orgId: string;
  /**
   * What the configurations belong to, as named in the message shown when
   * there is no URDF: "No URDF available for this device".
   */
  owner: "device" | "device type";
  /**
   * URL that opens the folder holding `urdf` in the configurations file
   * browser. The URDF's path links to it.
   */
  folderHref: (urdf: FileRecord) => string;
}

/** Whether the platform has a finished 3D asset for this upload of the URDF. */
function hasPreview(urdf: FileRecord): boolean {
  return (
    ingestPhase(readVisualizationAsset(urdf), urdf.upload_id) === "preview"
  );
}

/**
 * A 3D preview of one URDF among a device's or device type's configurations,
 * or what to do to get one. Renders no heading of its own; the page supplies it.
 *
 * Previews the first URDF, by path, that has been ingested as an asset; the
 * association's own URDFs come before inherited ones. An inherited URDF at a
 * path where the association holds an entry of its own is left out, as the
 * Configurations tab marks it Overridden. With URDFs but none ingested, it
 * names the first and says to ingest it.
 */
export function UrdfPreviewPanel({
  associationId,
  inheritedAssociationId,
  orgId,
  owner,
  folderHref,
}: UrdfPreviewPanelProps) {
  const ownQuery = useFilesWithExtensions(associationId, urdfFileTypes, orgId);
  const inheritedQuery = useFilesWithExtensions(
    inheritedAssociationId,
    urdfFileTypes,
    orgId,
  );
  // The association's own entries at the inherited URDFs' paths, which it uses
  // in place of the inherited ones. The same lookup marks those inherited
  // files Overridden in the Configurations tab, so the two agree.
  const inheritedPaths = React.useMemo(
    () => (inheritedQuery.data ?? []).map((urdf) => urdf.relative_path),
    [inheritedQuery.data],
  );
  const overridesQuery = useFilesAtPaths(
    inheritedAssociationId === undefined ? undefined : associationId,
    inheritedPaths,
    orgId,
  );
  // Until the lookup answers, no inherited URDF shows, so an overridden one
  // never shows for a moment.
  const inheritedUrdfs =
    inheritedPaths.length > 0 && overridesQuery.isPending
      ? []
      : (inheritedQuery.data ?? []).filter(
          (urdf) => !overridesQuery.data?.has(urdf.relative_path),
        );

  const urdfs = [...(ownQuery.data ?? []), ...inheritedUrdfs];
  const urdf = urdfs.find(hasPreview) ?? urdfs.at(0);
  const isLoading =
    ownQuery.isPending ||
    (inheritedAssociationId !== undefined &&
      (inheritedQuery.isPending ||
        (inheritedPaths.length > 0 && overridesQuery.isPending)));
  const error = ownQuery.error ?? inheritedQuery.error;

  let body: React.ReactNode;
  if (urdf) {
    // Keyed by file, so one URDF's loaded model never shows under another's name.
    body = (
      <UrdfPreview key={urdf.file_id} urdf={urdf} href={folderHref(urdf)} />
    );
  } else if (error) {
    body = <p className={styles.note}>Could not look for a URDF.</p>;
  } else if (isLoading) {
    body = <p className={styles.note}>Looking for a URDF…</p>;
  } else {
    body = (
      <p className={styles.note}>
        No URDF available for this {owner}. Upload one to configurations and
        ingest it as an asset to see a preview.
      </p>
    );
  }

  return <div className={styles.panel}>{body}</div>;
}

/** One URDF's preview, or the status of its conversion to a 3D asset. */
function UrdfPreview({ urdf, href }: { urdf: FileRecord; href: string }) {
  const { files } = useDomainServices();
  // Read from the file's own record, not from the listing that found it: the
  // record is current as soon as a conversion finishes, and this polls while
  // one is running.
  const assetQuery = useVisualizationAsset(urdf);
  const block = assetQuery.isPending
    ? readVisualizationAsset(urdf)
    : assetQuery.data;
  const phase = ingestPhase(block, urdf.upload_id);

  const assetFileQuery = useVisualizationAssetFile(
    urdf,
    block?.relative_path,
    phase === "preview",
  );
  const assetFile = assetFileQuery.data;

  const modelLoader = React.useMemo(() => new ModelLoader(files), [files]);
  const preview = useModelPreview(
    modelLoader,
    assetFile
      ? { fileId: assetFile.file_id, relativePath: assetFile.relative_path }
      : undefined,
  );

  // Whether the model load has finished: the model is on the canvas, or it
  // will not be because the load failed. Until then a pane covers the
  // viewport: building the renderer and loading the model each take seconds,
  // and an empty canvas reads as broken rather than busy.
  const [isModelLoadFinished, setIsModelLoadFinished] = React.useState(false);

  const path = (
    <Link className={styles.link} to={href}>
      {urdf.relative_path}
    </Link>
  );

  // "settings" is the ingest dialog's first step, where a URDF stays until it
  // has been ingested.
  if (phase === "settings") {
    return (
      <p className={styles.note}>
        {path} has not been ingested as an asset. Open it in the file list and
        ingest it to see a preview.
      </p>
    );
  }
  if (phase === "converting") {
    return <p className={styles.note}>Converting {path}…</p>;
  }
  if (phase === "failed") {
    return (
      <p className={styles.note}>
        {path} could not be converted. Open it in the file list to try again.
      </p>
    );
  }
  if (preview.error || assetFileQuery.error || assetFile === null) {
    return (
      <p className={styles.note}>The preview of {path} could not be loaded.</p>
    );
  }

  return (
    <>
      <p className={styles.note}>{path} preview:</p>
      <div className={styles.viewport}>
        {preview.state && (
          <BabylonPreview
            state={preview.state}
            modelLoader={modelLoader}
            onModelSettled={() => setIsModelLoadFinished(true)}
          />
        )}
        {!isModelLoadFinished && (
          <div className={styles.loading}>
            <Spinner
              size="lg"
              aria-label={`Loading the preview of ${urdf.relative_path}`}
            />
          </div>
        )}
      </div>
    </>
  );
}
